import * as React from "react";
import { useState, useContext, useCallback } from "react";
import Immutable from "immutable";
import styled from "styled-components";
import { Link } from "components/common/router";
import { Col, Label, Row } from "components/bootstrap";
import { MessageFields } from "views/components/messagelist";
import MessageDetailsTitle from "components/search/MessageDetailsTitle";
import { Icon, Spinner, Timestamp } from "components/common";
import Routes from "routing/Routes";
import FormatReceivedBy from "views/components/messagelist/FormatReceivedBy";
import FormatAssetList from "views/components/messagelist/FormatAssetList";
import useIsLocalNode from "views/hooks/useIsLocalNode";
import FieldTypesContext from "views/components/contexts/FieldTypesContext";
import useSearchConfiguration from "hooks/useSearchConfiguration";
import MessageFavoriteFieldsProvider from "views/components/contexts/MessageFavoriteFieldsProvider";
import MessageDetailAdditionalContextProvider from "views/components/contexts/MessageDetailAdditionalContextProvider";
import useFeature from "hooks/useFeature";
import DefaultMessageFields from "views/components/messagelist/MessageFields/DefaultMessageFields";
import MessageDetailProviders from "./MessageDetailProviders";
import MessageActions from "./MessageActions";
import MessageAugmentations from "./MessageAugmentations";
import MessageMetadata from "./MessageMetadata";
const _formatMessageTitle = (index, id) => {
  if (index) {
    return /* @__PURE__ */ React.createElement(Link, { to: Routes.message_show(index, id) }, id);
  }
  return /* @__PURE__ */ React.createElement("span", null, id, " ", /* @__PURE__ */ React.createElement(Label, { bsStyle: "warning" }, "Not stored"));
};
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 6px;
`;
const MessageDetail = ({
  disableMessageActions = false,
  disableSurroundingSearch = false,
  disableTestAgainstStream = false,
  expandAllRenderAsync = false,
  fields: messageFields = Immutable.List(),
  message,
  streams = Immutable.Map(),
  inputs = Immutable.Map(),
  showTimestamp = true,
  allStreams = Immutable.List()
}) => {
  var _a;
  const isFavoriteFieldsEnabled = useFeature("message_table_favorite_fields");
  const { config: searchesClusterConfig } = useSearchConfiguration();
  const [showOriginal, setShowOriginal] = useState(false);
  const { fields, index, id, decoration_stats: decorationStats } = message;
  const { gl2_source_node, gl2_source_input, associated_assets } = fields;
  const { isLocalNode } = useIsLocalNode(gl2_source_node);
  const { all } = useContext(FieldTypesContext);
  const _toggleShowOriginal = () => {
    setShowOriginal(!showOriginal);
  };
  const findFieldType = useCallback((field) => all.find((f) => f.name === field), [all]);
  if (expandAllRenderAsync) {
    return /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(Spinner, null)));
  }
  const streamIds = Immutable.Set(fields.streams);
  const messageStreams = streamIds.map((streamId) => streams.get(streamId)).filter((stream) => !!stream).toArray();
  let timestamp = null;
  if (showTimestamp) {
    timestamp = [];
    const rawTimestamp = fields.timestamp;
    timestamp.push(/* @__PURE__ */ React.createElement("dt", { key: `dt-${rawTimestamp}` }, "Timestamp"));
    timestamp.push(
      /* @__PURE__ */ React.createElement("dd", { key: `dd-${rawTimestamp}` }, /* @__PURE__ */ React.createElement(Timestamp, { dateTime: rawTimestamp, format: "complete" }))
    );
  }
  const messageTitle = _formatMessageTitle(index, id);
  return /* @__PURE__ */ React.createElement(
    MessageFavoriteFieldsProvider,
    {
      message,
      messageFields,
      isFeatureEnabled: isFavoriteFieldsEnabled
    },
    /* @__PURE__ */ React.createElement(MessageDetailAdditionalContextProvider, { isLocalNode }, /* @__PURE__ */ React.createElement(MessageDetailProviders, { message }, /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Row, { className: "row-sm" }, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(Header, null, /* @__PURE__ */ React.createElement(MessageDetailsTitle, null, /* @__PURE__ */ React.createElement(Icon, { name: "mail" }), "\xA0", messageTitle), /* @__PURE__ */ React.createElement(
      MessageActions,
      {
        index,
        id,
        fields,
        decorationStats,
        disabled: disableMessageActions,
        disableSurroundingSearch,
        disableTestAgainstStream,
        showOriginal,
        toggleShowOriginal: _toggleShowOriginal,
        searchConfig: searchesClusterConfig,
        streams: allStreams
      }
    )))), /* @__PURE__ */ React.createElement(Row, { id: `sticky-augmentations-boundary-${message.id}` }, /* @__PURE__ */ React.createElement(Col, { md: 3 }, /* @__PURE__ */ React.createElement(
      MessageMetadata,
      {
        timestamp,
        index,
        receivedBy: /* @__PURE__ */ React.createElement(
          FormatReceivedBy,
          {
            isLocalNode,
            inputs,
            sourceNodeId: gl2_source_node,
            sourceInputId: gl2_source_input
          }
        ),
        streams: messageStreams,
        assets: associated_assets ? /* @__PURE__ */ React.createElement(
          FormatAssetList,
          {
            associated_assets,
            fieldType: (_a = findFieldType("associated_assets")) == null ? void 0 : _a.type
          }
        ) : /* @__PURE__ */ React.createElement("div", null)
      }
    ), /* @__PURE__ */ React.createElement(MessageAugmentations, { message })), /* @__PURE__ */ React.createElement(Col, { md: 9 }, isFavoriteFieldsEnabled ? /* @__PURE__ */ React.createElement(MessageFields, null) : /* @__PURE__ */ React.createElement(DefaultMessageFields, { message, fields: messageFields }))))))
  );
};
export default MessageDetail;
